import React, { useEffect, useId, useRef, useState } from "react";

const MultiSelectDropdown = ({
  label = "Select items",
  options = [],
  selectedValues = [],
  onSelectionChange,
  displayProperty = "name",
  valueProperty = "id",
  secondaryProperty = "",
  disabled = false,
  loading = false,
  className = "",
  placeholder = "Select items to display",
  error = "",
  required = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const listboxId = useId();
  const buttonId = `${listboxId}-button`;
  const errorId = `${listboxId}-error`;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
        document.getElementById(buttonId)?.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, buttonId]);

  const handleToggle = () => {
    if (!disabled && !loading) {
      setIsOpen(!isOpen);
    }
  };

  const handleOptionToggle = (option) => {
    const originalValue = getOriginalOptionValue(option);
    const newSelectedValues = selectedValues.includes(originalValue)
      ? selectedValues.filter((value) => value !== originalValue)
      : [...selectedValues, originalValue];

    onSelectionChange(newSelectedValues);
  };

  const getOriginalOptionValue = (option) => {
    return typeof option === "object" && option[valueProperty] !== undefined
      ? option[valueProperty]
      : option;
  };

  const getOptionDisplay = (option) => {
    return typeof option === "object" && option[displayProperty] !== undefined
      ? String(option[displayProperty])
      : String(option);
  };

  const getOptionSecondary = (option) => {
    if (!secondaryProperty) return "";
    return typeof option === "object" && option[secondaryProperty] !== undefined
      ? String(option[secondaryProperty])
      : "";
  };

  const getSelectedDisplayText = () => {
    if (selectedValues.length === 0) return placeholder;

    if (selectedValues.length === 1) {
      const option = options.find(
        (opt) => getOriginalOptionValue(opt) === selectedValues[0]
      );
      return option ? getOptionDisplay(option) : placeholder;
    }

    return `${selectedValues.length} items selected`;
  };

  const errorMessage =
    typeof error === "string" ? error : error?.message || "";

  return (
    <div className={`w-full flex flex-col gap-y-2 ${className}`}>
      {label && (
        <label
          htmlFor={buttonId}
          className="block text-base font-medium text-black"
        >
          {label}
          {required && (
            <span className="text-red-700 ml-1" aria-hidden="true">
              *
            </span>
          )}
          {required && <span className="sr-only"> (required)</span>}
        </label>
      )}

      <div className="relative w-full" ref={dropdownRef}>
        <button
          type="button"
          id={buttonId}
          onClick={handleToggle}
          disabled={disabled || loading}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-controls={listboxId}
          aria-describedby={errorMessage ? errorId : undefined}
          className={`w-full px-4 py-3 text-left border border-gray-200 rounded-md bg-white text-base focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent ${
            disabled || loading
              ? "opacity-50 cursor-not-allowed"
              : "cursor-pointer hover:bg-gray-50"
          }`}
        >
          <span
            className={`${
              selectedValues.length === 0 ? "text-gray-700" : "text-gray-900"
            }`}
          >
            {loading ? "Loading..." : getSelectedDisplayText()}
          </span>
          <span
            className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none"
            aria-hidden="true"
          >
            <svg
              className={`w-5 h-5 text-gray-700 transition-transform ${
                isOpen ? "rotate-180" : ""
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </span>
        </button>

        {isOpen && (
          <ul
            id={listboxId}
            role="listbox"
            aria-multiselectable="true"
            aria-labelledby={buttonId}
            className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto p-0 m-0 list-none"
          >
            {loading ? (
              <li
                className="px-4 py-3 text-gray-700 text-sm"
                role="option"
                aria-selected="false"
                aria-disabled="true"
              >
                Loading...
              </li>
            ) : options.length === 0 ? (
              <li
                className="px-4 py-3 text-gray-700 text-sm"
                role="option"
                aria-selected="false"
                aria-disabled="true"
              >
                No options available
              </li>
            ) : (
              options.map((option) => {
                const originalValue = getOriginalOptionValue(option);
                const optionDisplay = getOptionDisplay(option);
                const optionSecondary = getOptionSecondary(option);
                const isSelected = selectedValues.includes(originalValue);
                const optionId = `${listboxId}-option-${originalValue}`;

                return (
                  <li
                    key={originalValue}
                    id={optionId}
                    role="option"
                    aria-selected={isSelected}
                  >
                    <button
                      type="button"
                      className="w-full px-4 py-3 hover:bg-gray-50 focus:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-600 cursor-pointer flex items-center text-left"
                      onClick={() => handleOptionToggle(option)}
                    >
                      <span
                        className={`mr-3 h-4 w-4 border border-gray-400 rounded flex items-center justify-center ${
                          isSelected
                            ? "bg-blue-700 border-blue-700 text-white"
                            : "bg-white"
                        }`}
                        aria-hidden="true"
                      >
                        {isSelected && (
                          <svg
                            className="w-3 h-3"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </span>
                      <span className="flex-1">
                        <span className="block text-gray-900 text-sm">
                          {optionDisplay}
                        </span>
                        {optionSecondary && (
                          <span className="block text-gray-700 text-xs mt-1">
                            {optionSecondary}
                          </span>
                        )}
                      </span>
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        )}

        {errorMessage && (
          <p id={errorId} className="text-sm text-red-700 mt-1" role="alert">
            {errorMessage}
          </p>
        )}
      </div>
    </div>
  );
};

export default MultiSelectDropdown;
